/**
 * **The measurable answers, over the matrix.** KANON `ankiety-klienta-2026-09-16`, view 01.
 *
 * ── Every chart is the library's ─────────────────────────────────────────────────────────────
 * Owner rule: charts go through `ReadoutChart` over the vendored bklit/visx renderer, never a
 * hand-drawn SVG. The sparklines in the canon's HTML are drawing aids and are not a specification
 * for a second renderer.
 *
 * ── Three fit; a fourth scrolls ──────────────────────────────────────────────────────────────
 * A form with up to three measurable questions gets the three-column grid Trendy uses, which is
 * what makes one date land on one pixel column across the row. A fourth cannot fit that row and
 * must not stack — the whole point of the strip is reading the three together — so the row becomes
 * a sideways scroller in the form's own question order, one card snapping to the port's start.
 *
 * ── One readout across all of them ───────────────────────────────────────────────────────────
 * `ReadoutSyncProvider` plus one `xDomain` handed to every card: hovering sleep moves the crosshair
 * on weight to the same occurrence. That is the question this strip exists to answer — "she missed
 * two sessions that week; was she sleeping?" — and it only works if every chart is drawn over the
 * same span, because a series that begins halfway across it must begin halfway across the plot.
 *
 * ── A two-option question is a RING over a strip of occurrences ──────────────────────────────
 * Owner, 16 September 2026, having been shown the line first: a plot that only ever sits on two
 * heights is a sawtooth, and a sawtooth reads as volatility rather than as „seven weeks out of
 * ten". So the card speaks Frekwencja's language instead — `shared/ui/chart/ReadoutRing` over the
 * first option's share, and one cell per occurrence beside it in the three DRAWINGS
 * `shared/ui/TrainingWeekStrip` and the attendance grid already use. Nothing here is hand-drawn:
 * the ring is the library's and two of the three cells are `DetailTab.module.css`'s own classes.
 *
 * bklit's `RingChart` draws several rings CONCENTRICALLY rather than as segments of one, so a
 * two-segment donut is not available from the library and is not hand-built: the ring carries the
 * first option's share and the LEGEND under the strip carries both counts and both drawings.
 *
 * ── Neither option is the good news ──────────────────────────────────────────────────────────
 * Same round: a two-option single choice is not necessarily yes/no — „zielony"/„niebieski" is just
 * as valid — so nothing here may paint one option green and the other red. Both are neutral ink,
 * told apart by a FILL against an OUTLINE, and the legend names which is which. The attendance
 * grid's „missed" cell wears `--danger` and is deliberately NOT reused for the second option.
 *
 * ── No caption ───────────────────────────────────────────────────────────────────────────────
 * Same round: the sentence under each card („Odstępy rysowane faktycznymi datami.") is advice about
 * how to read a chart, repeated once per card in a row that can hold six of them. It is gone.
 * `ChartCardShell` keeps the prop optional, because Trendy's captions COUNT something the plot does
 * not show.
 *
 * ── The ink says WHAT is measured, never where the card stands ───────────────────────────────
 * Owner rule, same round: blue is sleep, ember is energy, lime is an exercise result, everything
 * else is neutral white ink. This strip used to cycle four tokens by POSITION, which made one
 * question blue on one form and green on the next. `model/surveyHistory` decides the MEANING off
 * the catalog code; this file is where a meaning becomes a token, because `model/` may not name a
 * custom property.
 *
 * ── The axis is fitted, never anchored at zero ───────────────────────────────────────────────
 * Nobody weighs zero kilograms and no rating is zero out of five. An axis from zero puts every
 * reading in the top fifth of the box and says only "the number is large" — which the figure above
 * the plot already said.
 *
 * ── The ROW holds its box in all four states ─────────────────────────────────────────────────
 * Pending draws three placeholder cards; failed and "the form has no measurable question" draw ONE
 * card across the row's full width. Each card's own four states are `ChartCardShell`'s, reserve
 * included. An earlier revision dropped to zero cards on both of those and took the whole matrix up
 * with it.
 */

import { useTranslation } from 'react-i18next';

import type { SkeletonGate } from '~/shared/lib/useSkeletonGate';
import { ChartCardShell } from '~/shared/ui/chart/ChartCardShell';
import { ReadoutChart } from '~/shared/ui/chart/ReadoutChart';
import { ReadoutRing } from '~/shared/ui/chart/ReadoutRing';
import { ReadoutSyncProvider } from '~/shared/ui/chart/readoutSync';
import scrollbar from '~/shared/ui/scrollbar.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { SurveyChoiceCell, SurveyRingView, SurveySeriesView } from '~/features/survey-report';
import eyebrows from './cardEyebrow.module.css';
import detail from './DetailTab.module.css';
import { ChangeArrowIcon, HUE_INK, TONE_CLASS } from '~/features/survey-report';
import styles from './SurveysTab.module.css';

/** Beyond this the row cannot hold one date in one pixel column, so it scrolls instead. */
const CARDS_PER_ROW = 3;

/** The plot's height, reserved from the first frame. One token, quoted once. */
const PLOT_HEIGHT = 'var(--h-survey-chart)';


export interface SurveyChartsStripProps {
  readonly series: readonly SurveySeriesView[];
  readonly xDomain: readonly [Date, Date] | null;
  /**
   * The tab's own wait, over the same surveys read this strip draws from. It picks the branch, the
   * bars inside it and the `aria-busy` the shell publishes — the three are one value.
   */
  readonly gate: SkeletonGate;
  readonly failed: boolean;
  /**
   * The row keeps its box and says NOTHING in it.
   *
   * „Brak pomiarów w tym zakresie" is a statement about the RANGE, and it is only true when there
   * are surveys to have measured. A client who has never been sent one, and a read that did not
   * answer, are both already said once — in the pane, where the sentence carries what to do next —
   * and a strip repeating it is the same fact in three places.
   */
  readonly silent: boolean;
  readonly reducedMotion: boolean;
}

export function SurveyChartsStrip({
  series,
  xDomain,
  gate,
  failed,
  silent,
  reducedMotion,
}: SurveyChartsStripProps) {
  const { t } = useTranslation();
  // The read is open, or it failed, or the form asks nothing measurable: in none of those is there
  // a series to draw, and in all three the row still stands in its own box.
  //
  // The GATE decides which, not the read (21 September 2026): on `pending` the placeholders were
  // dropped the frame the answer landed, and every card underneath remounted anyway — see the key
  // below — so the shell's own hand-over was thrown away and its reveal was always empty.
  const cards = gate.waiting ? PLACEHOLDERS : series;
  const alone = failed || (!gate.waiting && series.length === 0);
  const scrolls = !alone && cards.length > CARDS_PER_ROW;

  /* A scrolling box is only reachable from the keyboard if something in it can take focus, and a
   * chart card holds one tab stop that is the plot itself — so the port is a named, focusable
   * region rather than a `<div>` a keyboard user can never move. It is only that when it actually
   * scrolls; three cards in a grid have nothing to scroll and would be a tab stop that does
   * nothing. One object rather than four attributes each asking the same question. */
  const port = scrolls
    ? {
        className: `${String(scrollbar.scrollbar)} ${String(styles.chartScroller)}`,
        role: 'group',
        tabIndex: 0,
        'aria-label': t('web:clientDetail.surveys.chartsLabel'),
      }
    : { className: String(detail.chartGrid) };

  return (
    <ReadoutSyncProvider>
      <div {...port}>
        {alone ? (
          <ChartCardShell
            className={`${String(detail.card)} ${String(styles.chartSpan)} ${gate.reveal}`}
            height={PLOT_HEIGHT}
            gate={gate}
            failed={failed}
            empty
            emptyTitle={silent ? null : t('web:clientDetail.surveys.chartEmpty')}
            failedTitle={silent ? null : t('web:clientDetail.surveys.failed')}
            /* The head's own reserve, with nothing in it: there is no question to name and no
              * reading to print, and the line box is what keeps the notice under it at the y a
              * loaded card's plot stands at. */
            head={<div className={`${String(detail.cardHead)} ${String(styles.chartHead)}`} />}
          >
            {null}
          </ChartCardShell>
        ) : (
          /* KEYED BY SLOT, not by the series in it, and that holds in BOTH states.
           *
           * Across the hand-over it is the only key that can: a placeholder has no question id, so
           * keys of `wait-0` and then of a question id are two different elements to React and
           * every card in the row was unmounted and rebuilt the moment the answer arrived — which
           * threw away the shell's gate mid-wait and made the hand-over impossible. Keying the
           * series and giving the placeholders the ids the series will have is not available:
           * nothing knows them until the read answers.
           *
           * Once loaded it is still right, because a card carries NOTHING that belongs to one
           * question. `ChartCard` holds no state, no ref and no effect; everything it draws is a
           * prop, and the crosshair the three share lives in `ReadoutSyncProvider` above them. So a
           * slot that changes which question it holds — a different form selected, a client with a
           * different set — re-renders with new props, and remounting instead would only replay the
           * ring's and the plot's own draw on a swap the trainer made. The slot is what persists:
           * three boxes that change what they hold. */
          cards.map((entry, slot) => (
            <ChartCard
              key={slot}
              series={entry}
              xDomain={xDomain}
              gate={gate}
              reducedMotion={reducedMotion}
            />
          ))
        )}
      </div>
    </ReadoutSyncProvider>
  );
}

/**
 * The row's shape while the read is open.
 *
 * Three empty series rather than a separate branch, so the card below has ONE code path and the
 * reserve cannot drift from the loaded card's. Their heads are blank, which is the point: a head
 * carrying a real figure over a skeleton plot would print „68,4 kg" above a shimmering box and then
 * move it when the exercise names landed.
 */
const PLACEHOLDERS: readonly SurveySeriesView[] = [0, 1, 2].map((slot) => ({
  key: `wait-${String(slot)}`,
  name: '',
  hue: 'neutral',
  unit: null,
  points: [],
  figureText: null,
  delta: null,
  deltaText: null,
  deltaTone: null,
  kind: 'number',
  empty: true,
}));

/**
 * The ring's geometry on a 420px strip card.
 *
 * Smaller than Frekwencja's 74px, because this ring shares its card with a ten-cell strip and a
 * two-line head rather than standing alone in a section. Numbers rather than tokens: `RingChart`
 * computes its arc in JavaScript — see `shared/ui/chart/ReadoutRing`.
 */
const RING = { diameter: 64, thickness: 9, durationMs: 950 } as const;

/**
 * One class per occurrence state. Three DRAWINGS, never three hues of one.
 *
 * The first option and „nobody answered" are the attendance grid's own cells — this app's two
 * places that draw a sequence of days draw it the same way. The second option is NOT that grid's
 * „missed" cell, which wears `--danger`: there is no bad answer here.
 */
const CELL_CLASS: Readonly<Record<SurveyChoiceCell['state'], string>> = {
  a: String(detail.attCellDone),
  b: String(styles.optCellB),
  none: String(detail.attCellFree),
};

function ChartCard({
  series,
  xDomain,
  gate,
  reducedMotion,
}: {
  readonly series: SurveySeriesView;
  readonly xDomain: readonly [Date, Date] | null;
  readonly gate: SkeletonGate;
  readonly reducedMotion: boolean;
}) {
  const { t } = useTranslation();
  const colour = HUE_INK[series.hue];

  return (
    <ChartCardShell
      /* `scroll-snap-align` unconditionally: snapping needs an ancestor that declares
       * `scroll-snap-type`, and the three-card grid declares none — so the class is inert there and
       * the row's shape stops being a prop threaded two components deep. */
      className={`${String(detail.card)} ${String(styles.chartCard)}`}
      height={PLOT_HEIGHT}
      gate={gate}
      failed={false}
      /* A ring card stands on its own occurrences and needs no shared time axis, so a `null`
       * `xDomain` — which only says "no line may be drawn" — must not empty it. */
      empty={series.empty || (series.kind !== 'binary' && xDomain === null)}
      emptyTitle={t('web:clientDetail.surveys.chartEmpty')}
      failedTitle={null}
      head={
        /* Two lines, and the box for both is reserved whether or not the second one is used — the
          * same reserve rule Trendy's chart head follows. The TITLE wraps rather than ellipsising:
          * owner, 16 September 2026, of „CZY WYKONAŁEŚ WSZYS…" — a card whose head is cut off has
          * stopped saying what it measures, which is the one thing a head is for. */
        <div className={`${String(detail.cardHead)} ${String(styles.chartHead)}`}>
          <em className={`${String(eyebrows.eyebrow)} ${String(eyebrows.eyebrowWrap)}`}>
            {series.name}
          </em>
          {series.figureText === null ? null : (
            <span className={styles.chartReading}>
              {/* The reading and what it is MEASURED IN. A bare „64,2" over a chart whose eyebrow is
                * the trainer's own question names no quantity; a rating carries no unit and says so
                * by naming its scale instead — and its figure is the window's AVERAGE, which is what
                * „/5 śr." says out loud. A yes/no figure is already a whole phrase — „70 % tak". */}
              <b className={detail.chartFigure}>
                {series.kind === 'binary'
                  ? series.figureText
                  : series.unit !== null
                    ? t('web:clientDetail.surveys.unitFigure', {
                        value: series.figureText,
                        unit: series.unit,
                      })
                    : series.kind === 'scale'
                      ? t('web:clientDetail.surveys.ratingAverage', { value: series.figureText })
                      : series.figureText}
              </b>
              {series.deltaText === null ? null : (
                <em
                  className={`${String(detail.chartDelta)} ${TONE_CLASS[series.deltaTone ?? 'flat']}`}
                >
                  <ChangeArrowIcon sign={Math.sign(series.delta ?? 0)} />
                  {t('web:clientDetail.surveys.deltaInRange', { delta: series.deltaText })}
                </em>
              )}
            </span>
          )}
        </div>
      }
    >
      {series.kind === 'binary' ? (
        <BinaryReadout series={series} reveal={gate.reveal} reducedMotion={reducedMotion} />
      ) : (
        <ReadoutChart
          className={gate.reveal}
          points={series.points}
          colour={colour}
          height={PLOT_HEIGHT}
          name={series.name}
          unit={series.unit ?? undefined}
          // Never zero — see the header.
          zeroBaseline={false}
          xDomain={xDomain ?? undefined}
          syncId={series.key}
          reducedMotion={reducedMotion}
        />
      )}
    </ChartCardShell>
  );
}

/**
 * The two-option card's drawing: the ring, the occurrences it is a share OF, and a legend.
 *
 * The three answer two different questions and none replaces the others — „how often" is the ring,
 * „when" is the strip, and „which drawing is which answer" is the legend. The strip is the only one
 * of the three that shows a run of one answer at the end of the window, and the legend is the only
 * one that can, because a fill and an outline mean nothing until something names them.
 *
 * The ring is neutral whatever the question is: the owner's colour rule spends blue on sleep and
 * ember on energy, and a choice is neither.
 */
function BinaryReadout({
  series,
  reveal,
  reducedMotion,
}: {
  readonly series: SurveyRingView;
  readonly reveal: string;
  readonly reducedMotion: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div className={`${String(styles.binary)} ${reveal}`}>
      <ReadoutRing
        percent={series.percent}
        diameter={RING.diameter}
        thickness={RING.thickness}
        durationMs={RING.durationMs}
        colour="var(--chart-series-neutral)"
        figure={series.countText}
        label={t('web:clientDetail.surveys.optionRingLabel', {
          name: series.name,
          option: series.optionA,
          count: series.countText,
        })}
        reducedMotion={reducedMotion}
      />

      <div className={styles.binaryStrip}>
        <ul
          className={detail.attCells}
          aria-label={t('web:clientDetail.surveys.optionStripLabel', { name: series.name })}
        >
          {series.cells.map((cell, index) => (
            <li
              key={`${series.key}-${String(index)}`}
              className={`${String(detail.attCell)} ${CELL_CLASS[cell.state]}`}
              title={cell.title}
            >
              <span className={hidden.srOnly}>{cell.title}</span>
            </li>
          ))}
        </ul>

        {/* Which drawing is which answer, and how many of each. A fill beside an outline says
          * nothing on its own, and this card may not say it with colour — see the header. */}
        <ul className={styles.binaryLegend}>
          {(
            [
              ['a', series.optionA, series.aCount],
              ['b', series.optionB, series.bCount],
            ] as const
          ).map(([state, option, count]) => (
            <li className={styles.binaryLegendItem} key={state}>
              <i
                className={`${String(detail.attCell)} ${CELL_CLASS[state]} ${String(styles.binarySwatch)}`}
                aria-hidden="true"
              />
              {option}
              <b className={styles.binaryCount}>{count}</b>
            </li>
          ))}
        </ul>

        {/* The window's two ends, so the strip reads as a period rather than as ten anonymous
          * squares. The dates BETWEEN them are on the matrix directly below. */}
        <p className={styles.binaryDates}>
          <span>{series.fromLabel}</span>
          <span>{series.toLabel}</span>
        </p>
      </div>
    </div>
  );
}

